import { noOpTokenStorage } from "./tokenStorage";
import {
  AuthClient,
  AuthClientConfig,
  AuthError,
  AuthErrorCode,
  AuthErrorResponse,
  SessionType,
  TokenExchangeResponse,
  TokenRefreshResponse,
} from "./types";

const TOKEN_EXCHANGE_PATH = "/token-exchange/";
const TOKEN_REFRESH_PATH = "/token-refresh/";
const NONCE_EXCHANGE_PATH = "/nonce-exchange/";
const LOGOUT_PATH = "/logout/";

const parseAuthError = async (response: Response): Promise<AuthError> => {
  let code: AuthErrorCode = "internal_error";
  let devHelp: unknown;
  try {
    const body = (await response.json()) as AuthErrorResponse;
    if (body?.code) {
      code = body.code;
    }
    devHelp = body?.dev_help;
  } catch {
    // Non-JSON error body — fall back to status-code defaults.
  }
  return new AuthError(code, response.status, code, devHelp);
};

/**
 * Creates a stateful auth client backed by the token-exchange REST endpoints.
 * State (access token, session type, in-flight refresh) lives in closure.
 *
 * Reactive, not proactive: we do not track token expiry locally. The server
 * is source of truth — when it returns 401 the Apollo errorLink calls
 * refresh() and retries the operation transparently.
 */
export const createAuthClient = (config: AuthClientConfig): AuthClient => {
  const {
    baseUrl,
    storage = noOpTokenStorage,
    onSessionExpired,
    credentials = "include",
    getRefreshTokenForBody,
    initialAccessToken = null,
    initialSessionType = null,
  } = config;

  let accessToken: string | null = initialAccessToken;
  let sessionType: SessionType | null = initialSessionType;
  let refreshInFlight: Promise<string | null> | null = null;

  /**
   * One-way latch: flips to `true` the first time a refresh attempt confirms
   * the session is dead (refresh endpoint returned non-2xx). Once set, every
   * subsequent `refresh()` call short-circuits with `null` and does NOT hit
   * the network or re-fire `onSessionExpired`.
   *
   * Without this, queries still in flight when the refresh fails each hit
   * `errorLink` → `refresh()` → `doRefresh()` in a tight loop between the
   * time `window.location.replace(LOGIN)` is called and the browser actually
   * unloads the page, producing a burst of `/token-refresh/` calls and
   * repeated `onSessionExpired` invocations.
   */
  let sessionExpired = false;

  const markSessionExpired = (): void => {
    if (sessionExpired) return;
    accessToken = null;
    sessionType = null;
    sessionExpired = true;
    storage.clearTokens();
    // Best-effort call to /logout/ to clear the HttpOnly refresh cookie
    // server-side so it isn't retried on next page load.
    post(LOGOUT_PATH).catch(() => {});
    onSessionExpired?.();
  };

  const resolveBaseUrl = (): string => (typeof baseUrl === "function" ? baseUrl() : baseUrl);

  const url = (path: string) => `${resolveBaseUrl()}${path}`;

  const post = (path: string, init?: Omit<RequestInit, "method" | "credentials">): Promise<Response> =>
    fetch(url(path), {
      method: "POST",
      credentials,
      ...init,
    });

  const getRefreshTokenBody = async (): Promise<string | undefined> => {
    const refreshToken = getRefreshTokenForBody
      ? await getRefreshTokenForBody()
      : await storage.getRefreshToken();
    return refreshToken ? JSON.stringify({ refreshToken }) : undefined;
  };

  /**
   * Apply a token-exchange response to client state. Shared between the
   * credentials flow (`signIn`) and the nonce flow (`nonceExchange`) —
   * both hit endpoints that return the same `TokenExchangeResponse`
   * shape, with `expiresIn` discriminating refreshable (Cognito) from
   * non-refreshable (legacy) sessions.
   */
  const applyExchangeResponse = async (data: TokenExchangeResponse): Promise<void> => {
    // Fresh session — clear the expired latch in case a prior session in
    // this tab died and the user re-authed without a hard reload.
    sessionExpired = false;
    accessToken = data.accessToken;

    if (data.expiresIn != null) {
      // Cognito / refreshable session. Access token stays in-memory only;
      // refresh token lives in HttpOnly cookie (web) or storage (mobile).
      sessionType = "refreshable";
      if (data.refreshToken) {
        await storage.setRefreshToken(data.refreshToken);
      }
      // Make sure any previously-persisted legacy token is cleared.
      await storage.setAccessToken(null);
    } else {
      // Legacy session. No refresh mechanism — persist access token so it
      // survives a page refresh.
      sessionType = "non-refreshable";
      await storage.setAccessToken(data.accessToken);
    }

    // Persist the discriminator so `hydrate()` can tell a booted Cognito
    // user from a logged-out one on cold start — the access token alone
    // isn't enough because refreshable sessions never persist it.
    await storage.setSessionType(sessionType);
  };

  const signIn = async (email: string, password: string): Promise<TokenExchangeResponse> => {
    const response = await post(TOKEN_EXCHANGE_PATH, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw await parseAuthError(response);
    }

    const data = (await response.json()) as TokenExchangeResponse;
    await applyExchangeResponse(data);
    return data;
  };

  const nonceExchange = async (token: string): Promise<TokenExchangeResponse> => {
    const response = await post(NONCE_EXCHANGE_PATH, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      throw await parseAuthError(response);
    }

    const data = (await response.json()) as TokenExchangeResponse;
    await applyExchangeResponse(data);
    return data;
  };

  const doRefresh = async (): Promise<string | null> => {
    if (sessionExpired) {
      // Latch is set — session is known-dead; a redirect is already in
      // flight. Don't hit the network, don't re-fire onSessionExpired.
      return null;
    }
    if (sessionType !== "refreshable") {
      return null;
    }

    const body = await getRefreshTokenBody();

    let response: Response;
    try {
      response = await post(TOKEN_REFRESH_PATH, {
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body,
      });
    } catch {
      // Transport-level failure (CORS, DNS, offline). Don't clear auth
      // state — the token may still be valid once connectivity returns.
      return null;
    }

    if (!response.ok) {
      // 401 invalid_token / 400 invalid_request → the backend has
      // explicitly rejected the refresh token. Clear auth state.
      // 500 internal_error → transient server issue; leave state intact.
      if (response.status === 401 || response.status === 400) {
        markSessionExpired();
      }
      return null;
    }

    const data = (await response.json()) as TokenRefreshResponse;
    accessToken = data.accessToken;
    sessionType = "refreshable";
    await storage.setRefreshToken(data.refreshToken ?? null);
    return data.accessToken;
  };

  const refresh = (): Promise<string | null> => {
    return (refreshInFlight ??= doRefresh().finally(() => {
      refreshInFlight = null;
    }));
  };

  const getAccessToken = (): string | null => accessToken;

  const hydrate = async (): Promise<void> => {
    const storedType = await storage.getSessionType();

    if (storedType === "refreshable") {
      // Cognito user coming back from cold boot. Access token stays null;
      // the first authenticated request will 401, the error link will
      // call `refresh()`, and the refresh token (HttpOnly cookie on web,
      // storage on mobile) decides whether the session is still alive.
      sessionType = "refreshable";
      return;
    }

    if (storedType === "non-refreshable") {
      const persisted = await storage.getAccessToken();
      if (persisted) {
        accessToken = persisted;
        sessionType = "non-refreshable";
      }
      return;
    }

    // No marker stored. Migration fallback for users who signed in before
    // the marker existed: infer from legacy signals. A persisted access
    // token means a legacy session; a persisted refresh token means a
    // Cognito session. (Web Cognito users pre-migration will have no
    // visible signal here — the HttpOnly cookie is invisible — and will
    // need to re-authenticate once.)
    const persistedAccess = await storage.getAccessToken();
    if (persistedAccess) {
      accessToken = persistedAccess;
      sessionType = "non-refreshable";
      return;
    }
    const persistedRefresh = await storage.getRefreshToken();
    if (persistedRefresh) {
      sessionType = "refreshable";
    }
  };

  const getSessionType = (): SessionType | null => sessionType;

  const logout = async (): Promise<void> => {
    try {
      await post(LOGOUT_PATH);
    } catch {
      // Best-effort — always clear local state even if the server call fails.
    }
    accessToken = null;
    sessionType = null;
    sessionExpired = true;
    await storage.clearTokens();
  };

  return {
    signIn,
    nonceExchange,
    refresh,
    getAccessToken,
    hydrate,
    getSessionType,
    logout,
  };
};
